import {
  ApiOperatorBusinessCasesGetCollectionParams,
  useApiOperatorBusinessCasesGetCollection,
} from "@fllite/api";
import { HydraCollection, MyTripsBusinessCase } from "@fllite/ui/types";

interface UseDashboardBusinessCasesParams {
  updatedAfter?: string;
  departureAfter?: string;
  departureBefore?: string;
  orderBy?: "updatedAt" | "departureAt";
  orderDirection?: "asc" | "desc";
  itemsPerPage?: number;
  notStatus?: string[];
  // Restrict to trips the customer has actually e-signed. The AI/RTP flow now
  // sets departureAt at trip-creation (parity with the web flow), so departure
  // window alone no longer implies "booked" — the dashboard has to ask for the
  // signed-only gate explicitly. It is deliberately NOT applied on the shared
  // operator collection, which /trips relies on to list the whole funnel.
  contractSignedOnly?: boolean;
}

export const useDashboardBusinessCases = ({
  updatedAfter,
  departureAfter,
  departureBefore,
  orderBy,
  orderDirection = "desc",
  itemsPerPage = 7,
  notStatus,
  contractSignedOnly,
}: UseDashboardBusinessCasesParams) => {
  // Typed against the generated param list so a misspelled or unsupported filter
  // key is a compile error rather than a query param the API silently ignores.
  const params: ApiOperatorBusinessCasesGetCollectionParams = {
    page: 1,
    itemsPerPage,
  };
  if (updatedAfter) {
    params["updatedAt[after]"] = updatedAfter;
  }
  if (departureAfter) {
    params["departureAt[after]"] = departureAfter;
  }
  if (departureBefore) {
    params["departureAt[before]"] = departureBefore;
  }
  // Spelled out per column rather than built as `order[${orderBy}]` — the
  // generated type keys these literally, and the template form would erase that.
  if (orderBy === "departureAt") {
    params["order[departureAt]"] = orderDirection;
  } else if (orderBy === "updatedAt") {
    params["order[updatedAt]"] = orderDirection;
  }
  if (notStatus && notStatus.length > 0) {
    params.notStatus = notStatus;
  }
  if (contractSignedOnly) {
    // API Platform ExistsFilter — registered on BusinessCase for contractSignedAt.
    params["exists[contractSignedAt]"] = true;
  }

  const response = useApiOperatorBusinessCasesGetCollection<
    HydraCollection<MyTripsBusinessCase>
  >(params, {
    request: { withHydra: true },
  });

  const data: MyTripsBusinessCase[] = response.data?.["hydra:member"] ?? [];

  return {
    data,
    isLoading: response.isLoading,
  };
};
